<script lang="ts" setup>
	import Dayjs from 'dayjs'
	import { useForm } from 'ant-design-vue/es/form'
	import usePurchases from '~/services/apis/purchases-info'
	import type { SummaryContext } from '~/components/Shared/summary-section.vue'
	import type { mediaType } from '~/components/Media/camera.vue'
	import { useResourcesApi } from '~/services/apis/resources'
	import { TimeOut } from '~/services/apis/timeout'
	import type { FormRecordsContext } from '~/composables/useFormsFields'

	export type DependantFormContext = {
		full_name: string
		phone: string
		email: string
		dob: string
		gender: string
		relationship: string
		photo: string | mediaType
	}

	const props = defineProps<{
		show: boolean
		staffPolicy: PolicyResponse | null
		policyId: string
		dependantId: string
		dependantType: '' | 'minor' | 'major'
		totalDependant: number
		pendingSlot: number
		activeSlot: number
	}>()

	const emit = defineEmits<{
		(event: 'update:show', param: boolean): void
		(event: 'update:add-dialog', param: boolean): void
		(event: 'back'): void
		(event: 'refresh'): void
		(event: 'update:activating', param: boolean): void
		(
			event: 'done',
			param: {
				key: string
				name: string
			}
		): void
	}>()

	const { addDependant } = usePurchases()

	const { disableDateProductAgeLimit } = useCustomDate()
	const { maritalStatus } = useStore()
	const { relationshipsList } = useResourcesApi()
	const { Gender } = useUser()
	const isSubmitting = shallowRef(false)
	const pendingUpdate = shallowRef(false)
	const activeSlotValue = ref(props.activeSlot)

	const dialogOption = reactive({
		submitted: false
	})

	const relationsShips = ref<SelectOptionContext[]>([])
	const formEl = ref<HTMLFormElement | null>(null)
	const activationSteps = ref<
		{
			key: 'dependant_details' | 'upload_hmo' | 'confirm_details' | ''
			paragraph: string
			title?: string
			active: boolean
		}[]
	>([
		{
			key: 'dependant_details',
			paragraph: 'Enter details of dependant as it appears on legal documents',
			active: true
		},
		{
			key: 'upload_hmo',
			paragraph: 'Submit a clear image for their HMO I.D.',
			active: false
		},
		{
			key: 'confirm_details',
			paragraph: ' Confirm the details for your dependants HMO I.D',
			title: 'Confirm Dependant HMO I.D',
			active: false
		}
	])

	const {
		isFamilyPlan: isFamilyPolicy,
		policyAuthor,
		canAccessHmo,
		dateAdded,
		provider
	} = usePolicyInfo(() => ({ policyInfo: props.staffPolicy }))
	const activeStep = computed(() => {
		return activationSteps.value.find(item => item.active)
	})

	// TODO: remove this after business conclusion
	const disableCoManager = true
	const formData = reactive<DependantPayload>({
		email: '',
		first_name: '',
		last_name: isFamilyPolicy.value ? policyAuthor.value.last_name : '',
		phone_number: '',
		relationship: null,
		marital_status: null,
		date_of_birth: '',
		gender: null,
		img_url: '',
		dependant_type: props.dependantType,
		policy_id: props.policyId,
		temp_id: props.dependantId,
		is_manager: false,
		pending_policy_id: '',
		phone: '',
		nin: ''
	})
	const { resetFields } = useForm(formData)

	const summary = computed<SummaryContext>(() => ({
		'Dependant Type': props.dependantType,
		'Full Name': `${formData?.first_name || ''} ${formData?.last_name || ''}`,
		'Email Address': formData?.email || '',
		'Phone Number': formData?.phone_number || '',
		'Date of birth': (formData?.date_of_birth && Dayjs(formData.date_of_birth).format('DD/MM/YYYY')) || '',
		Gender: formData.gender || '',
		relationship: formData.relationship || '',
		'Co-Manager': {
			value: formData.is_manager ? 'Yes' : 'No',
			disabled: !isFamilyPolicy.value
		}
	}))
	const validatingNin = shallowRef(false)

	const next = (key: (typeof activationSteps.value)[number]['key']) => {
		activationSteps.value.forEach(item => {
			item.active = item.key === key
		})
	}
	const proceed = (key: (typeof activationSteps.value)[number]['key']) => {
		formEl.value?.validate().then(() => {
			next(key)
		})
	}

	const submit = async () => {
		resolve(async () => {
			activeSlotValue.value = props.activeSlot

			const req = await addDependant({
				...formData,
				dependant_type: props.dependantType,
				policy_id: props.policyId,
				temp_id: props.dependantId,
				pending_policy_id: props.dependantId
			})
			if (req?.data) {
				emit('update:show', false)
				resolve(() =>
					useMixpanel().track('add_dpndts', {
						dpndts_type: props.dependantType
					})
				)
				next('dependant_details')
				if (activeSlotValue.value !== props.totalDependant) {
					activeSlotValue.value++
				}

				dialogOption.submitted = true
				pendingUpdate.value = true
			}
		}, isSubmitting)
	}
	const skip = async () => {
		next('dependant_details')
		resetFields()
		emit('refresh')
		pendingUpdate.value = false
		dialogOption.submitted = false
		await TimeOut()
		return true
	}
	const addAnotherDependant = () => {
		resetFields()
		next('dependant_details')
		emit('update:show', false)
		emit('update:add-dialog', true)
		emit('refresh')
		dialogOption.submitted = false
	}
	const closeModal = async () => {
		resetFields()
		proceed('dependant_details')
		emit('update:show', false)
		await refreshNuxtData('policies')
	}
	const ninElement = useTemplateRef('ninElement')
	const validateNin = async (fn: Function) => {
		if (ninElement.value && String(formData.nin)) {
			const isValid = await ninElement.value.validateNin()
			if (isValid) {
				fn()
			}
			return
		}
		fn()
	}

	const formFields = computed<Record<string, FormRecordsContext>>(() => ({
		first_name: {
			form_name: 'first_name',
			type: 'text',
			label: 'First name',
			placeholder: 'Enter first name of dependant',
			rules: { required: true }
		},
		last_name: {
			form_name: 'last_name',
			type: 'text',
			label: 'Last name',
			placeholder: 'Enter last name of dependant',
			rules: { required: true },
			disabled: isFamilyPolicy.value
		},
		phone_number: {
			form_name: 'phone_number',
			type: 'number',
			label: 'Phone number',
			placeholder: 'Enter phone number of dependant',
			rules: { required: true, min: 11, max: 12 },
			hidden: props.dependantType !== 'major'
		},
		email: {
			form_name: 'email',
			type: 'email',
			label: 'Email Address',
			placeholder: 'Enter email address of dependant',
			rules: { required: props.dependantType === 'major' },
			hidden: props.dependantType !== 'major'
		},
		date_of_birth: {
			form_name: 'date_of_birth',
			type: 'date',
			label: 'Date of birth',
			placeholder: 'DD/MM/YYYY',
			format: 'DD/MM/YYYY',
			disabledDate: current => disableDateProductAgeLimit(current, provider.value),
			rules: { required: true },
			hidden: props.dependantType === 'major'
		},
		relationship: {
			type: 'select',
			form_name: 'relationship',
			label: 'Relationship',
			placeholder: 'Enter relationship to dependant',
			rules: { required: true },
			options: relationsShips.value,
			class: 'capitalize',
			dropdownStyle: { textTransform: 'capitalize' }
		},
		gender: {
			type: 'select',
			form_name: 'gender',
			label: 'Gender',
			placeholder: 'Select gender',
			rules: { required: true },
			options: Gender,
			dropdownStyle: { textTransform: 'capitalize' }
		},
		marital_status: {
			type: 'select',
			form_name: 'marital_status',
			label: 'Marital Status',
			placeholder: 'Select Dependant marital status',
			rules: { required: true },
			options: maritalStatus,
			dropdownStyle: { textTransform: 'capitalize' },
			hidden: props.dependantType === 'major'
		}
	}))

	onMounted(async () => {
		const result = await relationshipsList({ is_family: isFamilyPolicy.value })
		if (Array.isArray(result)) {
			relationsShips.value = result
		}
	})
</script>

<template>
	<div>
		<dialogs :show :paragraph="activeStep?.paragraph" modal-class="!py-8 max-w-[626px]" @change="closeModal">
			<template #title>
				<span v-show="activeStep?.title">{{ activeStep?.title }}</span>
				<span v-show="!activeStep?.title">
					Add Dependant <span class="text-primary-2">({{ activeSlot + 1 }} of {{ totalDependant }})</span>
				</span>
			</template>
			<a-form
				ref="formEl"
				class="!mt-8"
				:model="formData"
				:validate-messages="antValidateMessages"
				:colon="false"
				@finish="() => validateNin(() => proceed(dependantType === 'minor' ? 'upload_hmo' : 'confirm_details'))"
			>
				<template v-if="activeStep?.key === 'dependant_details'">
					<forms-fields
						v-model="formData"
						:items="formFields"
						class="grid gap-x-5 gap-y-3 pb-5 md:grid-cols-2"
					>
						<template #label="{ form_name }">
							<span v-if="form_name === 'last_name'" class="form-label">
								Last name
								<span
									v-show="isFamilyPolicy"
									class="text-xs font-semibold tracking-wide text-warning-500"
								>
									(Fixed)
								</span>
							</span>
						</template>
					</forms-fields>

					<div class="mb-5">
						<forms-nin-field
							v-if="dependantType !== 'major'"
							ref="ninElement"
							v-model="formData.nin"
							v-model:validating="validatingNin"
							:validate-on-url="false"
							warning-text="Request for the staff dependant NIN to enable verification (optional)"
						/>
					</div>

					<div
						v-show="isFamilyPolicy && dependantType === 'major' && disableCoManager"
						class="rounded-[8px] border border-primary-50 bg-[#F7FDFB] p-5 pb-0"
					>
						<div class="inline-flex items-center gap-x-2 pb-2">
							<div class="flexed h-[24px] w-[24px] flex-none justify-center rounded-full bg-purple-50">
								<lazy-tools-icon
									dir="other"
									icon="user-circle-add"
									path-class="fill-purple-500"
									:width="14"
									:height="14"
								/>
							</div>
							<span class="font-semibold text-gray-800">Co-Manager?</span>
						</div>
						<p class="mb-5 text-xs font-medium leading-[18px] text-gray-600">
							Adding a <span class="font-semibold">Co-Manager</span> gives them access to manage this
							staff dependants, update their plan details, and assist with overseeing their plan's usage
							on their behalf.
						</p>
						<a-form-item>
							<a-switch v-model:checked="formData.is_manager" />
							<span class="ml-2 font-semibold text-gray-800">Yes</span>
						</a-form-item>
					</div>

					<shared-highlight-note
						v-if="dependantType === 'major'"
						note="An onboarding email will be sent to this dependant to complete the activation process."
						class="mt-10 border-warning-500 bg-warning-50"
					/>
					<div class="mt-6 flex flex-wrap items-center gap-5">
						<button
							type="button"
							name="upload photo"
							class="default-btn w-full flex-1 justify-center bg-gray-200"
							@click="$emit('back')"
						>
							Back
						</button>
						<ui-button
							type="submit"
							name="upload photo"
							data-testid="next-page"
							:loading="validatingNin"
							class="default-btn h-auto w-full flex-1 justify-center bg-primary-2"
						>
							Continue
						</ui-button>
					</div>
				</template>
				<template v-else-if="activeStep?.key === 'upload_hmo'">
					<media
						id="img_url"
						v-model:value="formData.img_url"
						btn-text="Get HMO ID"
						format=".jpg,.png,.jpeg"
						required
						camera-tip="Take photo in good lighting and make sure your face takes up to 75% of the screen."
						@change="proceed('confirm_details')"
					>
						<template #btn="{ disabled, process }">
							<div class="flex items-center justify-between gap-8">
								<button
									type="button"
									class="default-btn mt-5 w-full justify-center bg-gray-200"
									aria-label="previous screen"
									@click="proceed('dependant_details')"
								>
									Back
								</button>
								<button
									:disabled="disabled"
									class="default-btn mt-5 w-full justify-center bg-primary-2"
									aria-label="Generate HMO ID"
									data-testid="submit-form"
									@click="process"
								>
									Proceed
								</button>
							</div>
						</template>
					</media>
				</template>
			</a-form>

			<template v-if="activeStep?.key === 'confirm_details'">
				<div>
					<shared-summary-section :summary="summary" no-default-class bg="bg-primary-25 mb-8" />
					<media-img-preview
						v-if="formData.img_url"
						:src="formData.img_url"
						class="mb-8"
						wrapper-class="bg-success-50"
					/>
					<div class="flex items-center justify-between gap-8">
						<button
							type="button"
							class="default-btn mt-5 w-full justify-center bg-gray-200"
							aria-label="Generate HMO ID"
							@click="proceed(dependantType === 'minor' ? 'upload_hmo' : 'dependant_details')"
						>
							Back
						</button>
						<button
							type="submit"
							class="default-btn mt-5 w-full justify-center bg-primary-2"
							aria-label="Add Dependant"
							@click="submit"
						>
							Add Dependant
						</button>
					</div>
				</div>
			</template>
		</dialogs>

		<loader-with-text :active="isSubmitting" text="Adding Dependant" />

		<dialogs-success
			v-model:show="dialogOption.submitted"
			header="Dependant Added"
			header-class="max-w-sm mx-auto leading-snug"
			paragraph-class="bg-gray-50 p-8 flex flex-col items-center rounded text-center"
			@update:show="skip"
		>
			<template #paragraph>
				<div>
					<p v-show="props.dependantType === 'minor'">
						Your staff's dependant,
						<span class="text-primary-2">
							{{ formData.first_name || '---' }} {{ formData.last_name || '---' }} </span
						>, HMO ID has been generated.
					</p>
					<p v-show="props.dependantType === 'major'">
						We've sent an email to your staff's dependant,
						<span class="text-primary-2"
							>{{ formData.first_name || ' ---' }} {{ formData.last_name || '---' }}</span
						>, to activate their plan.
					</p>

					<p v-if="!canAccessHmo && props.dependantType === 'minor'" class="pt-2 text-gray-600">
						<span class="font-semibold">Waiting Period:</span> Note that this plan becomes active in
						{{ WAITING_PERIOD }} hours.
					</p>
				</div>
				<br />
				<my-cover-health-waiting-period-banner
					v-if="!canAccessHmo && props.dependantType === 'minor'"
					type="dependant"
					:date="dateAdded"
					class="mt-[30px] md:mt-[48px]"
				/>
			</template>

			<template #button>
				<div v-if="activeSlotValue < totalDependant" class="flex flex-col items-center justify-center">
					<button
						type="button"
						class="default-btn w-full justify-center bg-primary-2"
						@click="addAnotherDependant"
					>
						Add another dependant
					</button>
					<button
						type="button"
						class="default-btn w-full justify-center border-transparent text-primary-2"
						@click="skip"
					>
						Do this later
					</button>
				</div>

				<div v-else class="w-full space-y-4">
					<button type="button" class="default-btn w-full justify-center bg-primary-2" @click="skip">
						View dependants
					</button>
					<nuxt-link
						:to="`/mycover/custodian/${policyId}`"
						replace
						class="default-btn w-full justify-center text-primary-2"
					>
						Back to staff
					</nuxt-link>
				</div>
			</template>
		</dialogs-success>
	</div>
</template>
